import { Link } from "react-router-dom";
import { PhoneLink } from "./PhoneCTA";
import { Mail, Facebook, Twitter, Linkedin } from "lucide-react";

// Evaluated ONCE at module load so React sees a single, stable text child
// (rather than "text + {expr} + text" which produces adjacent JSX children
// that can't be hydrated against the DOM's parser-collapsed text node).
const COPYRIGHT_YEAR = new Date().getFullYear();
const LEGAL_LINE = `. \u00a9 ${COPYRIGHT_YEAR} \u2014 Educational content only, not medical advice.`;

export default function Footer() {
  return (
    <footer className="mt-24 bg-stone-900 text-stone-300">
      <div className="vv-container py-16 grid md:grid-cols-4 gap-10">
        <div>
          <img
            src="https://customer-assets-lqy194kg.emergentagent.net/job_therapy-finder-21/artifacts/uljvdqoh_gold-full-logo.webp"
            alt="VidaVital Medical"
            className="h-16 w-auto object-contain mb-4"
          />
          <p className="text-sm text-stone-400 leading-relaxed">
            Provider-directed peptide and wellness education. Licensed medical consultation required before any treatment.
          </p>
          <div className="mt-5 space-y-2">
            <PhoneLink location="footer" variant="dark" testId="footer-phone" className="text-base" />
            <a href="mailto:info@vidavitalmedical.com" className="flex items-center gap-2 text-sm text-stone-300 hover:text-white transition-colors">
              <Mail size={14} /> info@vidavitalmedical.com
            </a>
            <Link to="/request-consultation" data-testid="footer-schedule" className="vv-btn-primary text-sm mt-2 inline-flex">Schedule Consultation</Link>
            <div className="mt-5 flex items-center gap-3">
              <a
                href="https://x.com/medicalpeptidesmedicalpeptides"
                target="_blank" rel="noopener noreferrer"
                data-testid="footer-social-x"
                aria-label="Follow us on X"
                className="w-9 h-9 rounded-full border border-stone-700 grid place-items-center text-stone-400 hover:text-[color:var(--vv-gold)] hover:border-[color:var(--vv-gold)] transition-colors"
              >
                <Twitter size={15} />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61591829557886"
                target="_blank" rel="noopener noreferrer"
                data-testid="footer-social-facebook"
                aria-label="Follow us on Facebook"
                className="w-9 h-9 rounded-full border border-stone-700 grid place-items-center text-stone-400 hover:text-[color:var(--vv-gold)] hover:border-[color:var(--vv-gold)] transition-colors"
              >
                <Facebook size={15} />
              </a>
              <a
                href="https://www.linkedin.com/company/medical-peptides-powered-by-vidavital-medical"
                target="_blank" rel="noopener noreferrer"
                data-testid="footer-social-linkedin"
                aria-label="Follow us on LinkedIn"
                className="w-9 h-9 rounded-full border border-stone-700 grid place-items-center text-stone-400 hover:text-[color:var(--vv-gold)] hover:border-[color:var(--vv-gold)] transition-colors"
              >
                <Linkedin size={15} />
              </a>
            </div>
          </div>
        </div>
        <div>
          <div className="text-white font-medium mb-3">Explore</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/peptide-library" className="hover:text-white">Peptide Library</Link></li>
            <li><Link to="/treatment-goals" className="hover:text-white">Treatment Goals</Link></li>
            <li><Link to="/find-my-treatment" className="hover:text-white">Find My Treatment</Link></li>
            <li><Link to="/bloodwork" className="hover:text-white">Bloodwork</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-white font-medium mb-3">Clinic</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/locations" className="hover:text-white">Locations</Link></li>
            <li><Link to="/about" className="hover:text-white">About VidaVital</Link></li>
            <li><Link to="/how-it-works" className="hover:text-white">How Treatment Works</Link></li>
            <li><Link to="/request-consultation" className="hover:text-white">Schedule Consultation</Link></li>
            <li><Link to="/testimonials" data-testid="footer-testimonials" className="hover:text-white">Testimonials</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-white font-medium mb-3">Legal</div>
          <ul className="space-y-2 text-sm text-stone-400">
            <li><Link to="/privacy-policy" data-testid="footer-privacy-policy" className="hover:text-white">Privacy Policy</Link></li>
            <li><Link to="/terms-of-use" data-testid="footer-terms-of-use" className="hover:text-white">Terms of Use</Link></li>
            <li><Link to="/telehealth-consent" data-testid="footer-telehealth-consent" className="hover:text-white">Telehealth Consent</Link></li>
            <li><Link to="/medical-disclaimer" data-testid="footer-medical-disclaimer" className="hover:text-white">Medical Disclaimer</Link></li>
            <li><Link to="/admin/login" data-testid="footer-admin-login" className="hover:text-white">Staff Login</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-stone-800">
        <div className="vv-container py-6 text-xs text-stone-500 flex flex-col md:flex-row gap-3 md:justify-between">
          <div>
            MedicalPeptides.ai is an educational platform powered by <a href="https://www.vidavitalmedical.com" target="_blank" rel="noopener" className="hover:text-white underline">VidaVital Medical</a>{LEGAL_LINE}
          </div>
          <div>VidaVital Medical is a self-pay practice. Insurance not accepted for these services.</div>
        </div>
      </div>
    </footer>
  );
}
